// Структура: заголовки разделов (1–3 уровня, номер вычисляется), структурные элементы, приложения.
import { Node, mergeAttributes } from '@tiptap/core';
import { idAttr, registryNodeView } from './util';
import { STRUCT_KINDS, APP_STATUSES } from './constants';

/** Раздел (1) / подраздел (2) / пункт (3). Номер не хранится - его показывает плагин нумерации (data-num). */
export const StoHeading = Node.create({
  name: 'heading',
  group: 'block',
  content: 'inline*',
  defining: true,
  addAttributes() {
    return {
      ...idAttr(),
      level: {
        default: 1,
        parseHTML: (el) => Math.min(3, Math.max(1, Number(el.tagName.slice(1)) || 1)),
        renderHTML: () => ({}),
      },
    };
  },
  parseHTML() {
    return [1, 2, 3, 4, 5, 6].map((l) => ({ tag: 'h' + l, attrs: { level: Math.min(l, 3) } }));
  },
  renderHTML({ node, HTMLAttributes }) {
    const l = node.attrs.level as number;
    return ['h' + l, mergeAttributes(HTMLAttributes, { class: `sto-h sto-h${l}` }), 0];
  },
  addNodeView: registryNodeView('heading'),
});

/** Структурный элемент без номера: Введение, Выводы, Заключение, Термины…, Список источников (4.2.2). */
export const StructHeading = Node.create({
  name: 'structHeading',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      ...idAttr(),
      kind: {
        default: 'intro',
        parseHTML: (el) => el.getAttribute('data-kind') || 'intro',
        renderHTML: (a) => ({ 'data-kind': a.kind }),
      },
    };
  },
  parseHTML() { return [{ tag: 'div[data-type="struct-heading"]' }]; },
  renderHTML({ node, HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'struct-heading', class: 'sto-struct' }),
      STRUCT_KINDS[node.attrs.kind] || node.attrs.kind];
  },
  addNodeView: registryNodeView('structHeading'),
});

/** Приложение: контейнер «Приложение А (обязательное) / Название» + содержимое (4.16). */
export const Appendix = Node.create({
  name: 'appendix',
  group: 'appendix',
  content: 'appendixTitle block*',
  defining: true,
  isolating: true,
  draggable: false,
  addAttributes() {
    return {
      ...idAttr(),
      /** явная буква (null - по порядку) */
      letter: { default: null, parseHTML: (el) => el.getAttribute('data-letter') || null,
        renderHTML: (a) => (a.letter ? { 'data-letter': a.letter } : {}) },
      status: { default: APP_STATUSES[0], parseHTML: (el) => el.getAttribute('data-status') || APP_STATUSES[0],
        renderHTML: (a) => ({ 'data-status': a.status }) },
    };
  },
  parseHTML() { return [{ tag: 'section[data-type="appendix"]' }]; },
  renderHTML({ HTMLAttributes }) {
    return ['section', mergeAttributes(HTMLAttributes, { 'data-type': 'appendix', class: 'sto-appendix' }), 0];
  },
  addNodeView: registryNodeView('appendix'),
});

/** Название приложения (первая строка контейнера). Надпись «Приложение А (…)» - от плагина нумерации. */
export const AppendixTitle = Node.create({
  name: 'appendixTitle',
  content: 'inline*',
  defining: true,
  parseHTML() { return [{ tag: 'div.sto-appendix-title' }]; },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { class: 'sto-appendix-title' }), 0];
  },
});

export const structureExtensions = () => [StoHeading, StructHeading, Appendix, AppendixTitle];
